import React, { useState, useEffect } from 'react';
import { knowledgeBaseApi } from '../../services/knowledgeBaseApi';
import './DomainSelector.css';

interface DomainSelectorProps {
  selectedDomains: string[];
  onDomainsChange: (domains: string[]) => void;
  primaryDomain: string;
  onPrimaryDomainChange: (domain: string) => void;
  disabled?: boolean;
}

interface DomainInfo {
  key?: string;
  display_name?: string;
  label?: string;
  description?: string;
  usage_count?: number;
  custom?: boolean;
  has_rules?: boolean;
  framework_count?: number;
}

interface DomainCombination {
  domains: string[];
  description: string;
}

const DomainSelector: React.FC<DomainSelectorProps> = ({
  selectedDomains,
  onDomainsChange,
  primaryDomain,
  onPrimaryDomainChange,
  disabled = false
}) => {
  const [showHelp, setShowHelp] = useState(false);
  const [domainData, setDomainData] = useState<Record<string, DomainInfo>>({});
  const [loading, setLoading] = useState(true);
  const [commonCombinations, setCommonCombinations] = useState<DomainCombination[]>([]);

  // Fetch domain data from API
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        setLoading(true);
        const response = await knowledgeBaseApi.getAvailableDomains();
        if (response?.domains) {
          setDomainData(response.domains);
          
          // Generate common combinations based on available domains
          const combinations: DomainCombination[] = [];
          
          // Only add combinations if the domains exist
          if (response.domains.treasury && response.domains.gl) {
            combinations.push({ domains: ['treasury', 'gl'], description: 'Cash and banking reconciliation' });
          }
          if (response.domains.ar && response.domains.revenue) {
            combinations.push({ domains: ['ar', 'revenue'], description: 'Revenue and customer management' });
          }
          if (response.domains.ap && response.domains.operating_expenses) {
            combinations.push({ domains: ['ap', 'operating_expenses'], description: 'Expense and vendor management' });
          }
          if (response.domains.payroll && response.domains.operating_expenses && response.domains.gl) {
            combinations.push({ domains: ['payroll', 'operating_expenses', 'gl'], description: 'Payroll processing and posting' });
          }
          if (response.domains.consolidation && response.domains.intercompany && response.domains.financial_reporting) {
            combinations.push({ domains: ['consolidation', 'intercompany', 'financial_reporting'], description: 'Multi-entity reporting' });
          }
          if (response.domains.fixed_assets && response.domains.gl) {
            combinations.push({ domains: ['fixed_assets', 'gl'], description: 'Asset management and depreciation' });
          }
          
          setCommonCombinations(combinations);
        }
      } catch (error) {
        console.error('Failed to fetch domains:', error);
        // Set fallback data if API fails
        setDomainData({});
        setCommonCombinations([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDomains();
  }, []);

  const handleDomainToggle = (domain: string) => {
    if (selectedDomains.includes(domain)) {
      // Remove domain
      const newDomains = selectedDomains.filter(d => d !== domain);
      onDomainsChange(newDomains);
      
      // If this was the primary domain, select a new one
      if (primaryDomain === domain && newDomains.length > 0) {
        onPrimaryDomainChange(newDomains[0]);
      }
    } else {
      // Add domain
      const newDomains = [...selectedDomains, domain];
      onDomainsChange(newDomains);
      
      // If no primary domain set, make this one primary
      if (!primaryDomain || !selectedDomains.includes(primaryDomain)) {
        onPrimaryDomainChange(domain);
      }
    }
  };

  const applyCommonCombination = (domains: string[]) => {
    onDomainsChange(domains);
    if (domains.length > 0) {
      onPrimaryDomainChange(domains[0]);
    }
  };

  if (loading) {
    return (
      <div className="domain-selector">
        <div className="domain-selector-header">
          <label>Loading domains...</label>
        </div>
      </div>
    );
  }

  const sortedDomains = Object.entries(domainData).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="domain-selector">
      <div className="domain-selector-header">
        <label>Select Applicable Domains</label>
        <button 
          type="button"
          className="domain-help-btn"
          onClick={() => setShowHelp(!showHelp)}
        >
          {showHelp ? 'Hide' : 'Show'} Domain Guide
        </button>
      </div>

      {showHelp && commonCombinations.length > 0 && (
        <div className="domain-help-section">
          <h4>Common Domain Combinations</h4>
          <div className="domain-combinations">
            {commonCombinations.map((combo, idx) => (
              <button
                key={idx}
                type="button"
                className="domain-combo-btn"
                onClick={() => applyCommonCombination(combo.domains)}
                disabled={disabled}
              >
                <strong>{combo.domains.map(d => domainData[d]?.label || d).join(' + ')}</strong>
                <span>{combo.description}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="domain-grid">
        {sortedDomains.map(([key, info]) => (
          <div 
            key={key}
            className={`domain-item ${selectedDomains.includes(key) ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
            onClick={() => !disabled && handleDomainToggle(key)}
          >
            <input
              type="checkbox"
              checked={selectedDomains.includes(key)}
              onChange={() => {}}
              disabled={disabled}
            />
            <div className="domain-content">
              <div className="domain-label">
                {info.label}
                {info.custom && <span className="custom-badge">Custom</span>}
              </div>
              <div className="domain-description">{info.description}</div>
            </div>
            {selectedDomains.includes(key) && (
              <label className="primary-selector">
                <input
                  type="radio"
                  name="primary-domain"
                  checked={primaryDomain === key}
                  onChange={() => onPrimaryDomainChange(key)}
                  disabled={disabled}
                />
                Primary
              </label>
            )}
          </div>
        ))}
      </div>

      {selectedDomains.length > 0 && (
        <div className="selected-summary">
          <strong>Selected:</strong> {selectedDomains.map(d => domainData[d]?.label || d).join(', ')}
          <br />
          <strong>Primary:</strong> {primaryDomain ? (domainData[primaryDomain]?.label || primaryDomain) : 'None selected'}
        </div>
      )}
    </div>
  );
};

export default DomainSelector;